import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/utils/api';
import { setSelectedTeam } from '@/utils/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, UserPlus, Shield, Trash2, Mail, Eye, EyeOff } from 'lucide-react';

export default function TeamAdmins() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', name: '', password: '' });

  useEffect(() => {
    fetchTeam();
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const fetchTeam = async () => {
    try {
      const response = await api.get(`/teams/${teamId}`);
      setTeam(response.data);
      // Opening a team's admin page makes it the active "team context" too,
      // so it stays in sync with the sidebar selector/banner and with
      // whatever team-scoped page the security manager visits next.
      setSelectedTeam(response.data.id, response.data.name);
    } catch (error) {
      toast.error('Failed to load team');
    }
  };

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/users/admins', { params: { team_id: teamId } });
      setAdmins(response.data);
    } catch (error) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/users/admins', { ...formData, team_id: teamId });
      toast.success('Admin created successfully!');
      setShowForm(false);
      setFormData({ email: '', name: '', password: '' });
      fetchAdmins();
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm('Delete this admin? This cannot be undone.')) return;
    try {
      await api.delete(`/users/${adminId}`);
      toast.success('Admin deleted successfully!');
      fetchAdmins();
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A3C34]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/teams')}
          data-testid="back-to-teams-btn"
          className="mb-4 -ml-3 rounded-xl text-slate-600 hover:bg-slate-50 hover:text-slate-800"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Teams
        </Button>

        {/* Page Header */}
        <div className="pb-2 border-b border-slate-200/70 flex justify-between items-center flex-wrap gap-3">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 tracking-tight" data-testid="team-admins-title">
              Admins {team ? `— ${team.name}` : ''}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {team ? `${admins.length}/${team.max_admins} admins used` : 'Manage admins for this team'}
            </p>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            data-testid="add-admin-btn"
            className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            {showForm ? 'Cancel' : 'Add Admin'}
          </Button>
        </div>
      </div>

      {showForm ? (
        /* Add Admin Form — list disembunyikan selama form terbuka */
        <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
          <div className="pb-4 border-b border-slate-100 mb-6">
            <h2 className="text-xl font-bold font-heading text-slate-900">Add New Admin</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Isi detail akun admin baru</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full Name *</Label>
                <Input
                  id="name"
                  data-testid="admin-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Jane Doe"
                  className="w-full border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="admin-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="jane@company.com"
                  className="w-full border-slate-200 rounded-xl text-sm placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="admin-password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    placeholder="••••••••"
                    className="w-full border-slate-200 rounded-xl text-sm pr-10 placeholder:text-slate-300 placeholder:font-normal focus-visible:ring-[#1A3C34] focus-visible:border-[#1A3C34]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    data-testid="toggle-admin-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                    title={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="submit"
                data-testid="create-admin-btn"
                className="bg-[#1A3C34] hover:bg-[#142E27] text-white font-medium rounded-xl shadow-md transition-all duration-200 active:scale-[0.99]"
              >
                Create Admin
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setFormData({ name: '', email: '', password: '' });
                }}
                className="rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-800"
              >
                Batal
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <Card className="p-6 bg-white border-slate-200/80 shadow-sm">
          <div className="pb-4 border-b border-slate-100 mb-6">
            <h2 className="text-xl font-bold font-heading text-slate-900">Admins List</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Daftar admin yang terdaftar di team ini</p>
          </div>
          {admins.length === 0 ? (
            <p className="text-muted-foreground text-center py-8 text-sm">No admins yet. Add one above.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {admins.map((admin) => (
                <div
                  key={admin.id}
                  data-testid={`admin-card-${admin.id}`}
                  className="p-4 rounded-xl border border-slate-200 flex items-start justify-between gap-3 hover:bg-[#1A3C34]/5 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-[#1A3C34]/10 flex items-center justify-center shrink-0">
                      <Shield className="w-4 h-4 text-[#1A3C34]" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{admin.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Mail className="w-3 h-3" /> {admin.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(admin.id)}
                    data-testid={`delete-admin-btn-${admin.id}`}
                    className="p-1.5 rounded-lg text-slate-400 hover:bg-red-100 hover:text-red-700 transition-colors"
                    title="Delete Admin"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}